import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetTicketChatQuery, useGetChatQuery, useSendMessageMutation, useCloseTicketMutation } from '../../store/api/doctorApi';
import PageLoader from '../../components/PageLoader';
import { formatRelativeTime } from '../../utils/helpers';
import { confirmAction } from '../../utils/confirm';
import { showSuccess, showError } from '../../utils/toast';
import env from '../../config/env';

const DoctorTicketChat: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: ticketData, isLoading: ticketLoading } = useGetTicketChatQuery(id!);
  const { data: chatData, refetch } = useGetChatQuery(id!);
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [closeTicket, { isLoading: isClosing }] = useCloseTicketMutation();
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const ticket = ticketData?.data;
  const doctor = chatData?.data?.doctor;
  const messages = chatData?.data?.ticket?.ticket_messages || [];

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-refresh messages every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 3000);
    return () => clearInterval(interval);
  }, [refetch]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() && !selectedFile) return;
    
    const messageText = message.trim();
    setMessage('');
    const fileToSend = selectedFile;
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('message', messageText);
      if (fileToSend) {
        formDataToSend.append('file', fileToSend);
      }
      
      await sendMessage({ id: id!, formData: formDataToSend }).unwrap();
      refetch();
      setTimeout(() => scrollToBottom(), 100);
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to send message. Please try again.');
      setMessage(messageText);
      setSelectedFile(fileToSend);
    }
  };

  const handleCloseTicket = async () => {
    const confirmed = await confirmAction('Close Ticket', 'Are you sure you want to close this ticket?', 'Yes, close it!');
    if (confirmed) {
      try {
        await closeTicket(id!).unwrap();
        showSuccess('Ticket closed successfully!');
        navigate('/doctor/tickets');
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to close ticket. Please try again.');
      }
    }
  };

  const handleRefresh = () => {
    refetch();
    showSuccess('Messages refreshed');
  };

  if (ticketLoading) {
    return <PageLoader />;
  }

  if (!ticket) {
    return (
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
        <div className="alert alert-danger m-5">Ticket not found</div>
      </div>
    );
  }

  // Get ticket image from first message if available, or from ticket itself
  const ticketImage = ticket?.image || ticket?.file || (messages.length > 0 && messages[0]?.file && messages[0]?.sender !== 'doctor' ? messages[0].file : null);

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Ticket Messages</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/doctor/tickets'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Tickets</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-fluid">
            <div className="row justify-content-center">
              <div className="col-12 col-xl-10">
                {/* Ticket Details Card */}
                <div className="card border-bottom rounded-0">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <h2 className="fw-bold text-dark mb-2">{ticket.subject || 'No Subject'}</h2>
                        <div className="text-muted fs-6 mb-3">
                          Ticket: #{ticket.id} - {ticket.user?.name || ticket.user?.email || 'Unknown User'}
                        </div>
                        {ticketImage && (
                          <div className="mb-3">
                            <img 
                              src={`${env.apiUrl.replace('/api', '')}/uploads/tickets/${ticketImage}`} 
                              alt="Ticket attachment" 
                              className="rounded"
                              style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover' }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-light-success"
                          onClick={handleRefresh}
                          title="Refresh"
                        >
                          <span className="svg-icon svg-icon-2">
                            <i className="fa-solid fa-rotate-right"></i>
                          </span>
                        </button>
                        <button
                          className="btn btn-sm btn-light-success"
                          onClick={handleCloseTicket}
                          disabled={isClosing || ticket.status === 'Closed'}
                          title="Mark as Closed"
                        >
                          <span className="svg-icon svg-icon-2">
                            <i className="fa-solid fa-check"></i>
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Messages Thread */}
                <div className="card rounded-0">
                  <div className="card-body p-0">
                    <div 
                      ref={chatContainerRef}
                      className="p-4" 
                      style={{ 
                        maxHeight: '450px', 
                        overflowY: 'auto',
                        minHeight: '300px'
                      }}
                    >
                  {messages.length === 0 ? (
                    <div className="text-center text-muted py-10">
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((msg: any, index: number) => {
                      const isDoctor = msg.sender === 'doctor';
                      const showAvatar = index === 0 || messages[index - 1]?.sender !== msg.sender;
                      
                      return (
                        <div 
                          key={msg.id} 
                          className={`d-flex mb-5 ${isDoctor ? 'justify-content-end' : 'justify-content-start'}`}
                        >
                          <div className={`d-flex ${isDoctor ? 'flex-row-reverse' : 'flex-row'}`} style={{ maxWidth: '65%' }}>
                            {!isDoctor && showAvatar && (
                              <div className="me-3">
                                <div className="symbol symbol-35px symbol-circle">
                                  <img 
                                    src={ticket.user?.picture || '/media/avatars/blank.png'} 
                                    alt={ticket.user?.name || 'User'} 
                                    className="w-35px h-35px rounded-circle"
                                  />
                                </div>
                              </div>
                            )}
                            <div className={`d-flex flex-column ${isDoctor ? 'align-items-end' : 'align-items-start'}`}>
                              <div 
                                className={`p-4 rounded ${isDoctor ? 'bg-light-primary text-dark' : 'bg-light'}`}
                                style={{ 
                                  borderRadius: '8px',
                                  wordBreak: 'break-word'
                                }}
                              >
                                <div className="fw-bold mb-1">{isDoctor ? 'You' : ticket.user?.name || 'User'}</div>
                                <div className="mb-2">{msg.message}</div>
                                {msg.file && (
                                  <div className="mt-2">
                                    {msg.file_type === 'image' ? (
                                      <img 
                                        src={`${env.apiUrl.replace('/api', '')}/uploads/tickets/${msg.file}`} 
                                        alt="Attachment" 
                                        className="w-200px rounded"
                                        style={{ maxWidth: '200px', cursor: 'pointer' }}
                                        onClick={() => window.open(`${env.apiUrl.replace('/api', '')}/uploads/tickets/${msg.file}`, '_blank')}
                                      />
                                    ) : (
                                      <a 
                                        href={`${env.apiUrl.replace('/api', '')}/uploads/tickets/${msg.file}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-light-primary"
                                      >
                                        <span className="svg-icon svg-icon-2 me-1">
                                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M14 18V16H10V18L9 20H15L14 18Z" fill="currentColor" />
                                            <path opacity="0.3" d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 18H4V6H20V18Z" fill="currentColor" />
                                          </svg>
                                        </span>
                                        View File
                                      </a>
                                    )}
                                  </div>
                                )}
                                <div className="text-muted fs-7 mt-2">{formatRelativeTime(msg.created_at)}</div>
                              </div>
                            </div>
                            {isDoctor && showAvatar && (
                              <div className="ms-3">
                                <div className="symbol symbol-35px symbol-circle">
                                  <div className="symbol-label bg-base text-white fw-bold">
                                    {doctor?.name?.charAt(0)?.toUpperCase() || 'D'}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>
                </div>

                {/* Message Input */}
                <div className="card border-top rounded-0">
                  <div className="card-body">
                    {selectedFile && (
                      <div className="mb-3 d-flex align-items-center gap-2">
                        <span className="badge badge-light-primary">{selectedFile.name}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-icon btn-light"
                          onClick={() => {
                            setSelectedFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                        >
                          <span className="svg-icon svg-icon-1">
                            <i className="fa-solid fa-times"></i>
                          </span>
                        </button>
                      </div>
                    )}
                    <form onSubmit={handleSendMessage} className="d-flex align-items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="d-none"
                        onChange={handleFileSelect}
                        accept="image/*,video/*,.pdf,.doc,.docx"
                        disabled={ticket.status === 'Closed'}
                      />
                      <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type a message"
                        className="form-control form-control-solid"
                        disabled={isSending || ticket.status === 'Closed'}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(e);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => fileInputRef.current?.click()}
                        title="Attach File"
                        disabled={ticket.status === 'Closed'}
                      >
                        <span className="svg-icon svg-icon-2">
                          <i className="fa-solid fa-paperclip"></i>
                        </span>
                      </button>
                      <button
                        type="submit"
                        className="btn btn-base"
                        disabled={isSending || (!message.trim() && !selectedFile) || ticket.status === 'Closed'}
                      >
                        {isSending ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <span className="svg-icon svg-icon-2">
                            <i className="fa-solid fa-paper-plane text-white"></i>
                          </span>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorTicketChat;
